<script lang="ts" setup>
import { ref, watch, onBeforeMount, onMounted } from "vue";
import { usePGRESTAPIStore } from "../store/pgrest_api";
import PGRESTResult from "../classes/PGRESTResult";
import { ElMessage } from "element-plus";
import { Avatar, Bell, Edit, Delete, Document, Plus, RefreshLeft, CircleCheck, CircleClose, View, Watch } from "@element-plus/icons-vue";
import { storeToRefs } from "pinia";
import { useMainStore } from "../store/main";
import AddEdit_Guarantee from "../components/AddEdit_Guarantee.vue";
import UserType from "../enums/UserType";
import { useSort } from "../composables/sort";
import { useI18n } from "vue-i18n";
import Guarantee from "../classes/DB_Entities/Guarantee";
import { useGenericMethodsVariables } from "../composables/genericMethodsVariables";
import ChangeApprovalStatus from "../enums/ChangeApprovalStatus";
const { showError, showSuccess } = useGenericMethodsVariables();

const { t } = useI18n({ useScope: "global" });
const { onSortChange, sortColumn, sortOrder } = useSort();
const pgrestapi = usePGRESTAPIStore();
const mainStore = useMainStore();
const DeleteDialogVisible = ref(false);
const { globalPortfolioID } = storeToRefs(mainStore);
const { globalDebtorID } = storeToRefs(mainStore);
const { globalLoanAgreementID } = storeToRefs(mainStore);

let filterPending = ref(false);
let dataList = ref<any>([]);
let dataCount = ref(0);
let isLoading = ref(false);
let lastPage = ref(0);
let selectedRow = ref(0);
let addEditVisible = ref(false);
let filterDebtorFullname = ref<string>("");
let filterDebtorVAT = ref<string>("");
let filterGuaranteeFullname = ref<string>("");
let filterGuaranteeVAT = ref<string>("");
let filterPendingNew = ref(false);
let filterHideRejected = ref(true);

let DeleteSelectedRow = ref(0);
let deleteVisible = ref(false);
let isNewEntity = ref(false);
let viewOnly = ref(false);

let timer = ref<any>(null);

watch(globalPortfolioID, async () => {
  if (mainStore.loggedUser.username !== '') // Do not load if user logged out (changes the value of globalLoanAgreementID to "")
  await loadPage(1);
});

watch(globalDebtorID, async () => {
  if (mainStore.loggedUser.username !== '') // Do not load if user logged out (changes the value of globalLoanAgreementID to "")
  await loadPage(1);
});

watch(globalLoanAgreementID, async () => {
  if (mainStore.loggedUser.username !== '') // Do not load if user logged out (changes the value of globalLoanAgreementID to "")
  await loadPage(1);
});

onBeforeMount(() => {
  isLoading.value = true;
})

onMounted(async () => {
  isLoading.value = false;
  loadPage(1);
});

const onAdd = async () => {
  ElMessage({
    showClose: true,
    message: "Guarantee added Successfully",
    type: "success",
    duration: 1000,
  });

  addEditVisible.value = false;

  loadPage(lastPage.value);
};

const loadPage = async (page: number) => {
  isLoading.value = true;

  lastPage.value = page;
  page = page - 1;

  let filterUrl = "";

  if (filterDebtorFullname.value !== "") {
    filterUrl += `&fullname=ilike.*${filterDebtorFullname.value}*`;
  }
  if (filterGuaranteeFullname.value !== "") {
    filterUrl += `&guarantee_full_name=ilike.*${filterGuaranteeFullname.value}*`;
  }

  if (filterDebtorVAT.value !== "") {
    filterUrl += `&vat_number=ilike.*${filterDebtorVAT.value}*`;
  }

  if (filterGuaranteeVAT.value !== "") {
    filterUrl += `&guarantee_vat_number=ilike.*${filterGuaranteeVAT.value}*`;
  }

  if (filterPending.value) {
    filterUrl += "&pending_approvals=neq.0";
  }

  if (filterPendingNew.value) {
    filterUrl += "&approve_status=eq.0";
  }

  if (filterHideRejected.value) {
    filterUrl += "&approve_status=neq.2";
  }

  if (sortOrder.value !== "") {
    filterUrl += `&order=${sortColumn.value}.${sortOrder.value}`;
  }

  let selectedPortfolio = "";
  let selectedDebtor = "";
  let selectedLoanAgreement = "";
  let addedFilter = "";

  if (mainStore.globalPortfolioID !== "") {
    selectedPortfolio = mainStore.globalPortfolioID;
    addedFilter += "&portfolio=eq." + selectedPortfolio;
  }

  if (mainStore.globalDebtorID !== "") {
    selectedDebtor = mainStore.globalDebtorID;
    addedFilter += "&debtor_id=eq." + selectedDebtor;
  }

  if (mainStore.globalLoanAgreementID !== "") {
    selectedLoanAgreement = mainStore.globalLoanAgreementID;
    addedFilter += "&loan_agreement_id=eq." + selectedLoanAgreement;
  }

  if (mainStore.loggedUser.user_type == UserType.Agent) {
    addedFilter += `&allowed_users=cs.{${mainStore.loggedUser.id}}`;
  } else if (mainStore.loggedUser.user_type == UserType.Supervisor) {
    addedFilter += `&portfolio_allowed_users=cs.{${mainStore.loggedUser.id}}`;
  }

  let result: PGRESTResult = await pgrestapi.getPaginated(
    "view_mosguarantees_view1?select=*" + addedFilter + filterUrl,
    page
  );

  if (result.error) {
    showError(result.error.message);

    return;
  }

  if (result.headers)
    dataCount.value = parseInt(result.headers["content-range"].split("/")[1]);

  dataList.value = result.data;
  isLoading.value = false;
};

const onSave = async () => {
  selectedRow.value = -1;
  ElMessage({
    showClose: true,
    message: "Guarantee updated",
    type: "success",
    duration: 1000,
  });

  addEditVisible.value = false;

  loadPage(lastPage.value);
};

const onFailSave = async () => {
  selectedRow.value = -1;
  ElMessage({
    showClose: true,
    message: "Guarantee failed to update",
    type: "error",
    duration: 2000,
  });

  addEditVisible.value = false;
};

const handleClickAdd = () => {
  addEditVisible.value = true;
  isNewEntity.value = true;
  selectedRow.value = -1;
};

const handleClickEdit = (row: any) => {
  viewOnly.value = !isEditable(row);
  selectedRow.value = row.id;
  isNewEntity.value = false;
  addEditVisible.value = true;
};

const handleClickDelete = (row_id: number) => {
  DeleteDialogVisible.value = true;
  DeleteSelectedRow.value = row_id;
};

const deleteSelectedGuarantee = async () => {
  let result: PGRESTResult = new PGRESTResult();

  result = await pgrestapi.delete_data(
    "entity_rest_of_collaterals_changes?entity_id=eq." + DeleteSelectedRow.value
  );

  if (result.error) {
    showError(result.error.message);
    return;
  }

  result = await pgrestapi.delete_data("guarantees?id=eq." + DeleteSelectedRow.value);

  if (result.error) {
    showError(result.error.message);
    return;
  }

  onDelete();
};

const onDelete = async () => {
  
  showSuccess("Guarantee Deleted Successfully", 1000);

  DeleteDialogVisible.value = false;
  DeleteSelectedRow.value = -1;
  loadPage(lastPage.value);
};

const filterChanged = async (value: string) => {
  if (timer.value !== 0) {
    clearTimeout(timer.value);
  }

  timer.value = setTimeout(() => {
    loadPage(1);

    timer.value = 0;
  }, 500);
};

const localSortChange = async (sortObj: any) => {
  onSortChange(sortObj);
  await loadPage(lastPage.value);
};

const pendingFilterChange = async () => {
  await loadPage(1);
};

const onCloseAddEdit = () => {
  addEditVisible.value = false;
  selectedRow.value = -1;
};

const entityApprove = async (entity_id: number, accepted: boolean) => {
  console.log("entity id", entity_id, "approved", accepted);
  if (entity_id === undefined || entity_id === null) return;

  let result = await pgrestapi.patch(`${Guarantee.getTableNameStatic()}?id=eq.${entity_id}`, {
    approve_status: accepted ? ChangeApprovalStatus.Approved : ChangeApprovalStatus.Rejected,
  });

  ElMessage({
    showClose: true,
    message: `Guarantee${!accepted ? " not " : " "}approved`,
    type: "success",
    duration: 1000,
  });
  await loadPage(lastPage.value);
};

const isEditable = (row: any) => {
  return (row.debtor_approve_status === 1 && row.loan_agreement_approve_status === 1);
}
</script>

<template>
  <div class="content-wrapper" v-loading="isLoading">
    <el-row :gutter="10">
      <el-col :span="12">
        <h3 class="heading-page">
          {{ $t("GuaranteeView.guarantees") }} ({{ dataCount }})
        </h3>
      </el-col>
    </el-row>

    <div style="margin: 30px">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input v-model="filterDebtorFullname" class="client-input"
            :placeholder="$t('DebtorView.debtor_company_name')" @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-input v-model="filterDebtorVAT" class="client-input"
            :placeholder="$t('DebtorView.debor_tax_registration_nbr')" @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-input v-model="filterGuaranteeFullname" class="client-input"
            :placeholder="$t('GuaranteeView.guarantee_full_name')" @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-input v-model="filterGuaranteeVAT" class="client-input" :placeholder="$t('GuaranteeView.guarantee_vat')"
            @input="filterChanged" />
        </el-col>
      </el-row>
    </div>

    <div>
      <el-row :gutter="10" style="margin: 10px; margin-top: 15px">
        <el-col :span="12" class="realestate-add-new-btn">
          <el-switch v-model="filterPending" size="small" :active-text="$t('global.pending_changes')" inactive-text=""
            @change="pendingFilterChange()" style="float: left" />
          <el-switch v-model="filterPendingNew" size="small" :active-text="$t('global.pending_changes_new_entities')"
            inactive-text="" @change="pendingFilterChange()" style="float: left" />
          <el-switch v-model="filterHideRejected" size="small" :active-text="$t('global.hide_rejected_entities')"
            inactive-text="" @change="pendingFilterChange()" style="float: left" />
        </el-col>
        <el-col :span="12" class="realestate-add-new-btn">
          <el-button link type="primary" size="small" class="btn-add" @click="loadPage(lastPage)"
            style="float: right; margin: 2.5px" :icon="RefreshLeft" />
          <el-button link type="primary" size="small" class="btn-add" style="float: right; margin: 2.5px"
            @click="handleClickAdd" :icon="Plus" />
        </el-col>
      </el-row>
    </div>

    <el-table :data="dataList" stripe border table-layout="fixed" sortable="custom" @sort-change="localSortChange"
      size="small">
      <el-table-column sortable fixed prop="ar_sumbashs" :label="$t('GuaranteeView.loan_agreement')" />
      <el-table-column fixed prop="fullname" :label="$t('DebtorView.debtor_company_name')" sortable />
      <el-table-column fixed prop="vat_number" :label="$t('DebtorView.debor_tax_registration_nbr')" sortable />
      <el-table-column fixed prop="guarantee_full_name" :label="$t('GuaranteeView.guarantee_full_name')" sortable />
      <el-table-column fixed prop="guarantee_vat_number" :label="$t('GuaranteeView.guarantee_vat')" sortable />
      <el-table-column fixed prop="portf_name" :label="$t('DebtorDetailsView.portfolio')" sortable />

      <el-table-column sortable fixed prop="pending_approvals" :label="$t('global.pending_approvals')" />
      <el-table-column fixed="right" :label="$t('global.operations')" width="280">
        <template #default="scope">
          <el-button link type="primary" size="small" class="btn-detail" @click="handleClickEdit(scope.row)"
            :icon="isEditable(scope.row) ? Edit : View" />
          <el-tooltip v-if="scope.row.approve_status == 0 && mainStore.loggedUser.user_type === UserType.Agent"
            effect="dark" content="Pending Supervisor approval" placement="top">
            <el-button size="small" type="primary" class="btn-warn" :icon="Watch" circle />
          </el-tooltip>
          <el-button v-if="mainStore.loggedUser.user_type === UserType.Administrator" link type="primary" size="small"
            class="btn-delete" @click="handleClickDelete(scope.row.id)" :icon="Delete" />
          <span v-if="
            mainStore.loggedUser.user_type < UserType.Agent &&
            scope.row.approve_status === 0
          ">
            <el-button class="btn-accept_square" @click="entityApprove(scope.row.id, true)" :icon="CircleCheck" />
            <el-button class="btn-reject_square" @click="entityApprove(scope.row.id, false)" :icon="CircleClose" />
          </span>
          <el-popover v-if="scope.row.pending_approvals > 0" placement="top-start" title="" :width="155" trigger="hover"
            :content="$t('global.pending_changes')">
            <template #reference>
              <el-button :icon="Bell" circle size="small" style="background-color: coral; border: 0px; color: white" />
            </template>
          </el-popover>
          <el-popover v-if="scope.row.debtor_approve_status !== 1" placement="top-start" title="" :width="205"
            trigger="hover" :content="$t('global.debtor_not_approved')">
            <template #reference>
              <el-button :icon="Avatar" circle size="small"
                style="background-color: blueviolet; border: 0px; color: white" />
            </template>
          </el-popover>
          <el-popover v-if="scope.row.loan_agreement_approve_status !== 1" placement="top-start" title="" :width="265"
            trigger="hover" :content="$t('global.loan_agreement_not_approved')">
            <template #reference>
              <el-button :icon="Document" circle size="small"
                style="background-color: coral; border: 0px; color: white" />
            </template>
          </el-popover>
        </template>
      </el-table-column>
    </el-table>

    <div>
      <el-pagination size="small" layout="prev, pager, next" :page-size="10" :total="dataCount" @current-change="loadPage" />
    </div>

    <el-dialog v-if="addEditVisible" v-model="addEditVisible" @update:model-value="onCloseAddEdit"
      :title="isNewEntity ? 'Add Guarantee' : 'Guarantee Details'">
      <AddEdit_Guarantee :guaranteeid="selectedRow" :new-entity="isNewEntity" @close="onCloseAddEdit" @save="onAdd"
        :view_only="viewOnly" @fail-save="onFailSave" />
    </el-dialog>

    <el-dialog v-model="DeleteDialogVisible" title="Guarantee Property Delete" width="50%">
      <span>Are you sure you want to delete this Guarantee?</span>
      <template #footer #default="scope">
        <span class="dialog-footer">
          <el-button @click="DeleteDialogVisible = false">{{ $t('generic.cancel') }}</el-button>
          <el-button type="primary" @click="deleteSelectedGuarantee()">{{ $t('generic.delete') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>

</style>
